<template>
  <div id="what-we-believe" class="beliefs-page">
    <main>
      <section class="beliefs-section beliefs-section--hero" aria-labelledby="beliefs-title">
        <v-container class="beliefs-container beliefs-hero">
          <p class="beliefs-eyebrow">We Reckon</p>
          <h1 id="beliefs-title">What We Believe</h1>
          <div class="beliefs-lead">
            <p>At Reckon, we believe exceptional software begins with exceptional people.</p>
            <p>
              We set out to build more than a software engineering company. We wanted to create a
              place where talented people could solve meaningful problems, continue learning
              throughout their careers, and build lasting relationships along the way.
            </p>
            <p>
              That belief shapes everything we do, from the engineers we hire and the customers
              we serve to the experiences we share together. Because when you invest in people,
              great software follows.
            </p>
          </div>
        </v-container>
      </section>

      <section class="beliefs-section beliefs-section--charcoal" aria-labelledby="purpose-title">
        <v-container class="beliefs-container beliefs-split">
          <div>
            <p class="beliefs-eyebrow">Why We Exist</p>
            <h2 id="purpose-title">Building Exceptional People. Delivering Exceptional Software.</h2>
          </div>
          <div class="beliefs-copy">
            <p>Our mission is simple: build exceptional software by building exceptional teams.</p>
            <p>
              We believe the best solutions come from engineers who are empowered to learn,
              encouraged to collaborate, and trusted to take ownership of the work they do. That
              is why we invest in mentorship, continuous learning, conferences, traditions, and
              experiences that strengthen our team.
            </p>
            <p>
              When our people grow, our customers benefit from stronger partnerships, better
              collaboration, and innovative solutions that support their mission.
            </p>
          </div>
        </v-container>
      </section>

      <section class="beliefs-section beliefs-section--dark" aria-labelledby="core-beliefs-title">
        <v-container class="beliefs-container">
          <p class="beliefs-eyebrow">Core Beliefs</p>
          <h2 id="core-beliefs-title">The Principles Behind the Work</h2>
          <div class="belief-grid">
            <article v-for="(belief, index) in coreBeliefs" :key="belief.title" class="belief-card">
              <span class="belief-number">{{ stepNumber(index) }}</span>
              <h3>{{ belief.title }}</h3>
              <p class="belief-tagline">{{ belief.tagline }}</p>
              <p>{{ belief.body }}</p>
            </article>
          </div>
        </v-container>
      </section>

      <section class="beliefs-section beliefs-section--way" aria-labelledby="reckon-way-title">
        <v-container class="beliefs-container beliefs-split">
          <div>
            <p class="beliefs-eyebrow">The Reckon Way</p>
            <h2 id="reckon-way-title">Our Beliefs in Action</h2>
          </div>
          <div class="beliefs-copy">
            <p>
              The Reckon Way reflects our commitment to continuous learning, meaningful
              traditions, shared adventures, and the relationships that make great teams possible.
            </p>
            <p>Great teams are built one shared challenge, lesson, and experience at a time.</p>
            <div class="beliefs-actions">
              <v-btn large color="red" :to="{ name: 'reckon-way' }">Explore The Reckon Way</v-btn>
              <v-btn large outlined :to="{ name: 'beginnings' }">Read Our Story</v-btn>
            </div>
          </div>
        </v-container>
      </section>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '../components/Footer.vue';

export default {
  name: 'BeliefsView',
  components: { AppFooter },
  data: () => ({
    coreBeliefs: [
      {
        title: 'People Are the Advantage',
        tagline: 'Great technology starts with great people.',
        body: 'We invest in talented, curious people and give them opportunities to learn, lead, experiment, and grow. When people get better, everything we build gets better with them.',
      },
      {
        title: 'Stay Curious',
        tagline: 'There’s always another question worth asking.',
        body: 'We challenge assumptions, explore new ideas, share what we learn, and never get too comfortable with “the way we’ve always done it.”',
      },
      {
        title: 'Own the Outcome',
        tagline: 'We don’t just complete tasks. We take responsibility for what happens next.',
        body: 'That means understanding the problem, speaking up when something doesn’t make sense, following through, and caring about the result, not just our piece of it.',
      },
      {
        title: 'Build Together',
        tagline: 'The best solutions rarely come from a lone hero.',
        body: 'We bring different perspectives and strengths to the table, challenge each other respectfully, and share what we know. Great teams make everyone on them better.',
      },
      {
        title: 'Make It Better',
        tagline: 'Better software. Better processes. Better teammates. Better ourselves.',
        body: 'We believe progress comes from continuously looking for ways to improve and having the courage to change something when we find one.',
      },
      {
        title: 'Enjoy the Adventure',
        tagline: 'We take the work seriously. Ourselves, a little less so.',
        body: 'The relationships, experiences, traditions, and stories we build along the way matter. We want to do work we’re proud of with people we genuinely enjoy being around.',
      },
    ],
  }),
  methods: {
    stepNumber(index) {
      return String(index + 1).padStart(2, '0');
    },
  },
};
</script>

<style scoped>
.beliefs-page { background: #080808; }
.beliefs-section { padding: 84px 0; }
.beliefs-container { max-width: 1180px; }
.beliefs-section--hero { background: radial-gradient(circle at 82% 15%, #33110f 0, #090909 48%); }
.beliefs-section--charcoal {
  background: #111;
  border-bottom: 1px solid #2c2c2c;
  border-top: 1px solid #2c2c2c;
  color: #fff;
}
.beliefs-section--charcoal .beliefs-copy { color: #bdbdbd; }
.beliefs-section--dark { background: #080808; color: #fff; }
.beliefs-section--way { background: #171717; color: #fff; }
.beliefs-hero { max-width: 1040px; }
.beliefs-eyebrow {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 900;
  letter-spacing: .08em;
  margin: 0 0 12px;
  text-transform: uppercase;
}
h1, h2, h3 { font-family: "Oswald", sans-serif; }
h1 {
  color: #fff;
  font-size: clamp(2.5rem, 5vw, 4.8rem);
  font-weight: 700;
  letter-spacing: -.02em;
  line-height: 1.03;
  margin: 0;
}
h2 { font-size: clamp(2.3rem, 5vw, 4rem); line-height: 1.05; margin: 0; }
.beliefs-lead { color: #d2d2d2; font-size: clamp(1.08rem, 2vw, 1.35rem); line-height: 1.7; margin-top: 34px; max-width: 860px; }
.beliefs-copy { font-size: 1.08rem; line-height: 1.7; }
.beliefs-lead p, .beliefs-copy p { margin: 0 0 18px; }
.beliefs-split { display: grid; gap: 64px; grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr); }
.belief-grid { display: grid; gap: 18px; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-top: 42px; }
.belief-card { background: #141414; border: 1px solid #303030; padding: 30px; }
.belief-number { color: #e23232; font-family: "Oswald", sans-serif; font-weight: 900; }
.belief-card h3 { font-size: 1.8rem; line-height: 1.1; margin: 18px 0 12px; }
.belief-card p { color: #aaa; line-height: 1.65; }
.belief-card .belief-tagline { color: #fff; font-size: 1.05rem; font-weight: 700; }
.beliefs-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 30px; }
@media (max-width: 900px) {
  .beliefs-split { gap: 32px; grid-template-columns: 1fr; }
  .belief-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 600px) {
  .beliefs-section { padding: 58px 0; }
  h1 { font-size: clamp(2.35rem, 12vw, 3.35rem); }
  .belief-grid { grid-template-columns: 1fr; }
}
</style>
